import { Link, usePage } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Toaster } from "@/components/ui/sonner";
import { Beer, ChevronDown, FileUp, List, Menu, Plus, ScrollText, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

type SharedProps = {
  currentUser: {
    id: number;
    username: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
};

const navLinks = [
  { label: "Home", href: "/" },
];

const producerMenuItems = [
  { label: "All Producers", href: "/producers", description: "View and search all producers", icon: List },
  { label: "New Producer", href: "/producers/new", description: "Add a new producer", icon: Plus },
];

const categoryMenuItems = [
  { label: "All Categories", href: "/categories", description: "View and search all categories", icon: List },
  { label: "New Category", href: "/categories/new", description: "Add a new category", icon: Plus },
];

const boozeMenuItems = [
  { label: "All Booze", href: "/boozes", description: "View and search all booze", icon: List },
  { label: "New Booze", href: "/boozes/new", description: "Add a new booze entry", icon: Plus },
];

const reportMenuItems = [
  { label: "Generate Reports", href: "/reports/generate", description: "Create and download reports", icon: ScrollText },
  { label: "Upload Reports", href: "/reports/upload", description: "Import collection, producers, or bottles from CSV", icon: FileUp },
];

function NavListItem({
  href,
  title,
  description,
  icon: Icon,
  className,
}: {
  href: string;
  title: string;
  description: string;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <li className="w-full">
      <NavigationMenuLink asChild className="h-auto w-full justify-start">
        <Link
          href={href}
          className={cn(
            "flex w-full flex-col items-start gap-1 select-none rounded-md p-3 no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
        >
          <div className="flex items-center gap-2 text-sm font-medium leading-none">
            {Icon && <Icon className="size-4" />}
            {title}
          </div>
          <span className="text-xs text-muted-foreground text-left">
            {description}
          </span>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { currentUser } = usePage<{ props: SharedProps }>().props as unknown as SharedProps;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProducerOpen, setMobileProducerOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileBoozeOpen, setMobileBoozeOpen] = useState(false);
  const [mobileReportOpen, setMobileReportOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-stone-800 bg-stone-950/95 backdrop-blur supports-[backdrop-filter]:bg-stone-950/80">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Link href="/" className="flex items-center gap-2 font-bold text-lg tracking-tight text-white">
              <Beer className="size-5 text-amber-500" />
              BoozeHound
            </Link>

            <div className="hidden items-center gap-1 md:flex">
              {navLinks.map((link) => (
                <Button key={link.href} variant="ghost" size="sm" className="text-white hover:bg-accent hover:text-accent-foreground" asChild>
                  <Link href={link.href}>{link.label}</Link>
                </Button>
              ))}

              <NavigationMenu viewport={false}>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-white hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground">Categories</NavigationMenuTrigger>
                    <NavigationMenuContent className="absolute left-0 top-full mt-1.5 rounded-md border bg-popover text-popover-foreground shadow">
                      <ul className="grid w-[280px] gap-1 p-2">
                        {categoryMenuItems.map((item) => (
                          <NavListItem
                            key={item.href}
                            href={item.href}
                            title={item.label}
                            description={item.description}
                            icon={item.icon}
                          />
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-white hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground">Producers</NavigationMenuTrigger>
                    <NavigationMenuContent className="absolute left-0 top-full mt-1.5 rounded-md border bg-popover text-popover-foreground shadow">
                      <ul className="grid w-[280px] gap-1 p-2">
                        {producerMenuItems.map((item) => (
                          <NavListItem
                            key={item.href}
                            href={item.href}
                            title={item.label}
                            description={item.description}
                            icon={item.icon}
                          />
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-white hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground">Booze</NavigationMenuTrigger>
                    <NavigationMenuContent className="absolute left-0 top-full mt-1.5 rounded-md border bg-popover text-popover-foreground shadow">
                      <ul className="grid w-[280px] gap-1 p-2">
                        {boozeMenuItems.map((item) => (
                          <NavListItem
                            key={item.href}
                            href={item.href}
                            title={item.label}
                            description={item.description}
                            icon={item.icon}
                          />
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="bg-transparent text-white hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground">Reports</NavigationMenuTrigger>
                    <NavigationMenuContent className="absolute left-0 top-full mt-1.5 rounded-md border bg-popover text-popover-foreground shadow">
                      <ul className="grid w-[280px] gap-1 p-2">
                        {reportMenuItems.map((item) => (
                          <NavListItem
                            key={item.href}
                            href={item.href}
                            title={item.label}
                            description={item.description}
                            icon={item.icon}
                          />
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            {currentUser ? (
              <>
                <Button variant="ghost" size="sm" className="text-white hover:bg-accent hover:text-accent-foreground" asChild>
                  <Link href="/profile/edit">{currentUser.firstName ?? currentUser.username}</Link>
                </Button>
                <Button variant="ghost" size="sm" className="text-white hover:bg-accent hover:text-accent-foreground" asChild>
                  <Link href="/session" method="delete" as="button">Log out</Link>
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" className="text-white hover:bg-accent hover:text-accent-foreground" asChild>
                <Link href="/session/new">Log in</Link>
              </Button>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-white hover:bg-accent hover:text-accent-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>

        {mobileOpen && (
          <div className="border-t border-stone-800 bg-stone-950 px-4 py-3 md:hidden">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Button key={link.href} variant="ghost" size="sm" className="justify-start text-white hover:bg-accent hover:text-accent-foreground" asChild>
                  <Link href={link.href} onClick={() => setMobileOpen(false)}>{link.label}</Link>
                </Button>
              ))}

              <Button
                variant="ghost"
                size="sm"
                className="justify-between text-white hover:bg-accent hover:text-accent-foreground"
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
              >
                Categories
                <ChevronDown className={cn("size-4 transition-transform", mobileCategoryOpen && "rotate-180")} />
              </Button>
              {mobileCategoryOpen && (
                <div className="ml-4 flex flex-col gap-1">
                  {categoryMenuItems.map((item) => (
                    <Button key={item.href} variant="ghost" size="sm" className="justify-start gap-2 text-stone-400 hover:text-white hover:bg-stone-800" asChild>
                      <Link href={item.href} onClick={() => setMobileOpen(false)}>
                        <item.icon className="size-4" />
                        {item.label}
                      </Link>
                    </Button>
                  ))}
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="justify-between text-white hover:bg-accent hover:text-accent-foreground"
                onClick={() => setMobileProducerOpen(!mobileProducerOpen)}
              >
                Producers
                <ChevronDown className={cn("size-4 transition-transform", mobileProducerOpen && "rotate-180")} />
              </Button>
              {mobileProducerOpen && (
                <div className="ml-4 flex flex-col gap-1">
                  {producerMenuItems.map((item) => (
                    <Button key={item.href} variant="ghost" size="sm" className="justify-start gap-2 text-stone-400 hover:text-white hover:bg-stone-800" asChild>
                      <Link href={item.href} onClick={() => setMobileOpen(false)}>
                        <item.icon className="size-4" />
                        {item.label}
                      </Link>
                    </Button>
                  ))}
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="justify-between text-white hover:bg-accent hover:text-accent-foreground"
                onClick={() => setMobileBoozeOpen(!mobileBoozeOpen)}
              >
                Booze
                <ChevronDown className={cn("size-4 transition-transform", mobileBoozeOpen && "rotate-180")} />
              </Button>
              {mobileBoozeOpen && (
                <div className="ml-4 flex flex-col gap-1">
                  {boozeMenuItems.map((item) => (
                    <Button key={item.href} variant="ghost" size="sm" className="justify-start gap-2 text-stone-400 hover:text-white hover:bg-stone-800" asChild>
                      <Link href={item.href} onClick={() => setMobileOpen(false)}>
                        <item.icon className="size-4" />
                        {item.label}
                      </Link>
                    </Button>
                  ))}
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="justify-between text-white hover:bg-accent hover:text-accent-foreground"
                onClick={() => setMobileReportOpen(!mobileReportOpen)}
              >
                Reports
                <ChevronDown className={cn("size-4 transition-transform", mobileReportOpen && "rotate-180")} />
              </Button>
              {mobileReportOpen && (
                <div className="ml-4 flex flex-col gap-1">
                  {reportMenuItems.map((item) => (
                    <Button key={item.href} variant="ghost" size="sm" className="justify-start gap-2 text-stone-400 hover:text-white hover:bg-stone-800" asChild>
                      <Link href={item.href} onClick={() => setMobileOpen(false)}>
                        <item.icon className="size-4" />
                        {item.label}
                      </Link>
                    </Button>
                  ))}
                </div>
              )}
            </nav>
            <div className="mt-3 flex flex-col gap-2 border-t border-stone-800 pt-3">
              {currentUser ? (
                <>
                  <Button variant="ghost" size="sm" className="justify-start text-white hover:bg-accent hover:text-accent-foreground" asChild>
                    <Link href="/profile/edit" onClick={() => setMobileOpen(false)}>
                      {currentUser.firstName ?? currentUser.username}
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" className="justify-start text-white hover:bg-accent hover:text-accent-foreground" asChild>
                    <Link href="/session" method="delete" as="button">Log out</Link>
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="sm" className="justify-start text-white hover:bg-accent hover:text-accent-foreground" asChild>
                  <Link href="/session/new" onClick={() => setMobileOpen(false)}>Log in</Link>
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>
      <Toaster richColors position="top-center" />
    </div>
  );
}
